/**
 * Runtime configuration accessor for the Explorer SPA.
 *
 * CRA inlines `process.env.REACT_APP_*` at compile time, which used to make every build
 * environment-specific: `make publish-frontend` compiled a fresh bundle per environment with that
 * environment's API URL, Auth0 tenant and Mapbox token baked in. That is why the SPA could not be
 * promoted alongside the API container — there was no single artifact to promote.
 *
 * Config now comes from `window.__APP_CONFIG__`, set by `public/config.js`, which is loaded
 * synchronously ahead of the bundle and rewritten per environment at deploy time. One bundle is
 * built once in ci and copied unchanged to alpha and production.
 *
 * `process.env.REACT_APP_*` remains the fallback for every key, so a bare `npm start` and the
 * Jest/Playwright suites keep reading `web/.env` with no changes.
 * Deployed builds have the window values populated and never reach the fallback.
 */

const FALLBACKS = {
  BASE_API_URL: process.env.REACT_APP_BASE_API_URL,
  AUTH0_DOMAIN: process.env.REACT_APP_AUTH0_DOMAIN,
  AUTH0_CLIENT_ID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  AUTH0_AUDIENCE: process.env.REACT_APP_AUTH0_AUDIENCE,
  MAPBOX_API_TOKEN: process.env.REACT_APP_MAPBOX_API_TOKEN,
  // Border-gateway migration (see ops/systems/border-gateway/README.md and
  // src/context/{AuthContext,Auth0AuthBridge,GatewayAuthBridge}.js). AUTH_MODE defaults to ""
  // here, which App.js treats as "auth0-spa" -- today's behavior, unchanged, until a deployment
  // opts in. AUTH_APP_URL is only read when AUTH_MODE is "gateway".
  AUTH_MODE: process.env.REACT_APP_AUTH_MODE,
  AUTH_APP_URL: process.env.REACT_APP_AUTH_APP_URL,
  // Which base map (lib/maps/basemap.js): "mapbox" or "offline". Unset = Mapbox when a token is
  // configured, the offline style otherwise -- so local development needs no Mapbox account.
  MAP_BASEMAP: process.env.REACT_APP_MAP_BASEMAP,
};

/**
 * Read one configuration value.
 *
 * Resolution order: `window.__APP_CONFIG__[key]` if it is a non-empty string, then the matching
 * `REACT_APP_*` build-time value, then "". Blank-not-missing is the test that matters: config.js
 * ships with every key present but empty, so a key existing says nothing about it being set.
 *
 * @param {keyof typeof FALLBACKS} key
 * @returns {string} the configured value, or "" when unset everywhere
 */
export function getConfig(key) {
  const runtime =
    typeof window !== "undefined" && window.__APP_CONFIG__
      ? window.__APP_CONFIG__[key]
      : undefined;
  if (typeof runtime === "string" && runtime.trim() !== "") {
    return runtime.trim();
  }
  const fallback = FALLBACKS[key];
  return typeof fallback === "string" ? fallback.trim() : "";
}

/** Keys this module knows about — useful for a deploy-time completeness check. */
export const CONFIG_KEYS = Object.keys(FALLBACKS);

export default getConfig;
